import React, { useState, useEffect } from "react";
import { imageDimensionSet } from "../../helpers";
import "./index.scss";
import ToggleModule, { ToggleType } from "../Toggle";

type FeaturedPlayers = {
  Players: Player[];
};

type Player = {
  firstName: string;
  lastName: string;
  profileLink: string;
  profileImage: string;
  backgroundImageDesktop: string | null;
  playerType: ToggleType;
  bio: string;
  stats: Stat[] | null;
};

type Stat = {
  heading: string;
  value: string;
  label: string;
};

export default ({ Players }: FeaturedPlayers) => {
  const checkGirls = Players.filter(p => p.playerType.value === "G");
  const [Player, updatePlayer] = useState<Player>(
    checkGirls.length ? checkGirls[0] : Players[0]
  );
  const defaultToggleValue = Players.slice(0, 2)
    .reduce((toggles: ToggleType[], player: Player) => {
      if (player.playerType.value === "G") {
        toggles.unshift({
          ...player.playerType,
          active: true
        });
      } else {
        toggles.push({
          ...player.playerType,
          active: false
        });
      }
      return toggles;
    }, [])
    .map((player, key) => ({ ...player, key }));

  const [section, showSection] = useState<null | "bio" | "overview">("bio");
  const [toggles, updateToggle] = useState<ToggleType[]>(defaultToggleValue);

  const updatePlayerType = (playerType: string) => {
    if (playerType !== Player.playerType.value) {
      showSection(null);
      setTimeout(() => {
        showSection("bio");
        updatePlayer(Players.filter(p => p.playerType.value === playerType)[0]);
        updateToggle(
          toggles.map(t => ({ ...t, active: t.value === playerType }))
        );
      }, 250);
    }
  };

  const backgroundImageCSS = `
  .featured-player__wrapper:before {
    background-image: url(${
      Player.backgroundImageDesktop
        ? imageDimensionSet({
            url: Player.backgroundImageDesktop,
            width: 2440,
            height: 1415
          })
        : "unset"
    });
    }
  `;

  useEffect(() => {
    const headEl = document.querySelector("head");
    const styleNode = document.createElement("style");
    styleNode.innerHTML = backgroundImageCSS;
    if (headEl) headEl.appendChild(styleNode);
  }, [Player]);

  const widthReg = /width=[0-99]+&/gi;
  const heightReg = /height=[0-99]+&/gi;
  const profilePic = Player.profileImage
    .replace(widthReg, "width=390&")
    .replace(heightReg, "height=500&");

  return (
    <div className="featured-player__wrapper">
      <div
        className="featured-player__section"
        style={{ backgroundColor: "transparent" }}
      >
        <div className="page-section__inner">
          <div className="featured-player">
            <h2>Featured Player</h2>
            {toggles.length > 1 && (
              <ToggleModule updateToggle={updatePlayerType} toggles={toggles} />
            )}
            <div
              className={`featured-player__container ${Player.playerType.value}`}
            >
              <div
                style={{
                  perspective: "2000px"
                }}
              >
                <ul className="featured-player__nav-links">
                  {Player.bio && Player.stats && Player.stats.length > 0 && (
                    <li
                      className={`featured-player__nav-link${
                        section === "bio" ? " active" : ""
                      }`}
                    >
                      <button
                        onClick={() => {
                          showSection("bio");
                        }}
                      >
                        Bio
                      </button>
                    </li>
                  )}
                  {Player.stats && Player.stats.length > 0 && (
                    <li
                      className={`featured-player__nav-link${
                        section === "overview" ? " active" : ""
                      }`}
                    >
                      <button
                        onClick={() => {
                          showSection("overview");
                        }}
                      >
                        Overview
                      </button>
                    </li>
                  )}
                </ul>
                <div
                  className={`featured-player__player-tile${
                    section === "overview" ? " flip" : ""
                  }`}
                >
                  <a
                    href={Player.profileLink}
                    className="featured-player__player-tile-image"
                    style={{ backgroundImage: `url("${profilePic}")` }}
                  ></a>
                  <div className="featured-player__player-tile-info">
                    <div className="featured-player__player-tile-name">
                      <span>{Player.firstName}</span>
                      {Player.lastName}
                    </div>
                    <a
                      href={Player.profileLink}
                      className="featured-player__player-tile-profile-link"
                    >
                      Profile
                    </a>
                  </div>
                  {Player && Player.stats && (
                    <ul className="featured-player__overview-stats">
                      {Player.stats.slice(0, 4).map((stat, key) => (
                        <li
                          key={key}
                          className="featured-player__overview-stat"
                        >
                          <div className="featured-player__overview-stat-heading">
                            {stat.heading}
                          </div>
                          <div>
                            <div className="featured-player__overview-stat-value">
                              {stat.value}
                            </div>
                            <div className="featured-player__overview-stat-label">
                              {stat.label}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="featured-player__player-tile-name__container">
                    <div className="featured-player__player-tile-name flip">
                      <span>{Player.firstName}</span>
                      {Player.lastName}
                    </div>
                    <a
                      href={Player.profileLink}
                      className="featured-player__player-tile-profile-link"
                    >
                      Profile
                    </a>
                  </div>
                </div>
                <div
                  className={`featured-player__player-tile-overview-link${
                    section === "overview" ? " active" : ""
                  }`}
                  onClick={() => {
                    if (section === "bio") {
                      showSection("overview");
                    } else {
                      showSection("bio");
                    }
                  }}
                >
                  Overview
                </div>
              </div>
              <div className="featured-player__content-area">
                <section
                  className={`featured-player__section featured-player__content-bio${
                    section === "bio" ? " show" : " hide"
                  }`}
                >
                  <p>{Player.bio}</p>
                </section>
                <section
                  className={`featured-player__section featured-player__overview${
                    section === "overview" ? " show" : " hide"
                  }`}
                >
                  {Player.stats && Player.stats.length > 0 && (
                    <ul className="featured-player__overview-stats">
                      {Player.stats.slice(0, 4).map((stat, key) => (
                        <li
                          key={key}
                          className="featured-player__overview-stat"
                        >
                          <div className="featured-player__overview-stat-heading">
                            {stat.heading}
                          </div>
                          <div className="featured-player__overview-stat-value">
                            {stat.value}
                          </div>
                          <div className="featured-player__overview-stat-label">
                            {stat.label}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
